<template>
  <FriedDropdown
    class="module-switch-dropdown"
    :main-axis-offset="8"
    :cross-axis-offset="8"
    :same-width-as-ref="isSmallScreenAndDown"
  >
    <template #trigger>
      <div :class="['module-dropdown-toggle', { compact }]" data-external="module-dropdown-toggle">
        <div :class="['brand-container', { dsr: isDealRoomModuleActive }]">
          <FriedIcon :icon="isDealRoomModuleActive ? 'door' : 'contracts'" :size="IconSize.Small" />
        </div>
        <template v-if="isSmallScreenAndDown || !compact">
          <FriedH4 class="module-dropdown-toggle-text">{{
            isDealRoomModuleActive ? $t('deal_room') : $t('module_switcher_contract_room')
          }}</FriedH4>
          <div class="chevron-container">
            <FriedIcon
              class="chevron-down-icon"
              :size="IconSize.Small"
              icon="chevron-up-and-down"
            />
          </div>
        </template>
      </div>
    </template>
    <template #content>
      <div>
        <div
          v-for="product in products"
          :key="product.type"
          :class="['product-item', { active: activeModule === product.type }]"
          @click="handleClick(product.type)"
        >
          <div :class="['brand-container', { dsr: product.type === ModuleName.DealRooms }]">
            <FriedIcon :icon="product.icon" :size="IconSize.Small" />
          </div>
          <div class="product-text">
            <FriedH4 class="product-text-title">{{ product.text }}</FriedH4>
            <CheckmarkIcon v-if="activeModule === product.type" />
          </div>
        </div>
      </div>
    </template>
  </FriedDropdown>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { storeToRefs } from 'pinia';
import type { IconName } from '@getaccept/fried-tofu';
import { IconSize } from '@getaccept/fried-tofu';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { ModuleName } from '@getaccept/lib-shared/src/entity/app-modules/enums/app-module';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import CheckmarkIcon from '../assets/checkmark.svg';
import { useModulesStore } from '../../modules/store/modules.store';
import { useSidebarStore } from './store/sidebar.store';

export default defineComponent({
  name: 'ProductSwitcher',
  components: {
    CheckmarkIcon,
  },
  emits: ['set-module-type'],
  setup(_, { emit }) {
    const sidebarStore = useSidebarStore();
    const { compact } = storeToRefs(sidebarStore);
    const { isDealRoomModuleActive, activeModule } = storeToRefs(useModulesStore());

    const { isSmallScreenAndDown } = useBreakpoints();

    const products = computed<{ text: string; icon: IconName; type: ModuleName }[]>(() => [
      {
        text: t('module_switcher_contract_room'),
        icon: 'contracts',
        type: ModuleName.Contracts,
      },
      {
        text: t('deal_room'),
        icon: 'door',
        type: ModuleName.DealRooms,
      },
    ]);

    const handleClick = (type: ModuleName) => {
      if (type === activeModule.value) {
        return;
      }
      emit('set-module-type', type);
    };

    return {
      ModuleName,
      compact,
      IconSize,
      products,
      handleClick,
      isSmallScreenAndDown,
      isDealRoomModuleActive,
      activeModule,
    };
  },
});
</script>

<style lang="scss" scoped>
.module-switch-dropdown {
  margin: var(--spacing-50) 0;
}

.module-dropdown-toggle {
  cursor: pointer;
  border-radius: var(--radius-medium);
  display: flex;
  height: 3rem;
  padding: var(--spacing-50);
  align-items: center;
  border: 1px solid var(--gray-89);
  transition:
    0.2s width ease-in-out,
    0.2s margin ease-in-out;

  &.compact {
    @media screen and (min-width: $sm) {
      width: 3rem;
      margin-left: var(--spacing-25);
    }
  }

  .module-dropdown-toggle-text {
    color: var(--text-gray);
    margin-left: var(--spacing-50);
    flex: auto;
    overflow: hidden;
    white-space: nowrap;
  }

  .chevron-container {
    color: var(--text-gray);
    display: flex;
    padding-left: var(--spacing-50);
  }

  &:hover {
    background: var(--gray-96);
  }
}

.product-item {
  display: flex;
  align-items: center;
  padding: var(--spacing-50);
  padding-right: var(--spacing-100);
  border-radius: var(--radius-medium);
  width: 100%;
  cursor: pointer;

  @media screen and (min-width: $sm) {
    width: 15rem;
  }

  &.active {
    background: var(--gray-96);
  }

  &:hover {
    background: var(--gray-96);
  }

  &:active {
    background: var(--gray-94);
  }
}

.product-text {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: var(--spacing-50);
  flex: auto;

  .product-text-title {
    color: var(--text-gray);
  }
}

.brand-container {
  height: 2rem;
  min-width: 2rem;
  border-radius: var(--radius-medium);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--signature-gradient);
  color: white;

  &.dsr {
    background: var(--base-blue);
  }
}
</style>
